import { BallisticMovementEffect } from "../engine/effects/ballisticMovementEffect";
import { MeleeHitMovementEffect } from "../engine/effects/meleeHitMovementEffect";
import { MovementEffect } from "../engine/effects/movementEffect";
import type { XYEffect } from "../engine/effects/xyEffect";
import type { PhysicalThing } from "../engine/physicalThing";
import { XY } from "../engine/xy";
import type { EffectManager } from "./effectManager";
import { scaleEngineTimeToUi } from "./effectManager";

/**
 * Computes the interpolated board-space fractional position for a MovementEffect
 * at the given progress. Handles linear, ballistic (parabolic arc), and melee-hit
 * (bounce forward/back) movement types.
 */
export function interpolateMovementBoard(
	effect: MovementEffect,
	percentDone: number,
): { x: number; y: number } {
	const ox = effect.origin.x;
	const oy = effect.origin.y;
	const dx = effect.destination.x - ox;
	const dy = effect.destination.y - oy;

	if (effect instanceof BallisticMovementEffect) {
		const arcFactor = -4 * percentDone * percentDone + 4 * percentDone;
		return { x: ox + dx * percentDone, y: oy + dy * percentDone - arcFactor * effect.height };
	} else if (effect instanceof MeleeHitMovementEffect) {
		const f = percentDone <= 0.5 ? percentDone : 1 - percentDone;
		return { x: ox + dx * f, y: oy + dy * f };
	}

	return { x: ox + dx * percentDone, y: oy + dy * percentDone };
}

/**
 * Computes the animation progress (0-1) for a MovementEffect given the EffectManager's
 * elapsed time. Accounts for the engine→UI time scaling.
 */
export function getEffectPercentDone(effect: MovementEffect, manager: EffectManager): number {
	const scaledMaxTime = scaleEngineTimeToUi(effect.time);
	const elapsed = manager.getElapsedTime(effect);
	return Math.min(Math.max(elapsed / scaledMaxTime, 0), 1);
}

/**
 * Board-space draw position for an XYEffect at the given elapsed animation time.
 * Stationary effects return their xy; effects with a travel set lerp toward the
 * destination over the travel time (engine units, scaled to UI time like MovementEffect).
 */
export function resolveXYEffectDrawPosition(effect: XYEffect, elapsedMs: number): XY {
	const travel = effect.getTravel();
	if (!travel) return effect.xy;
	const scaledTime = scaleEngineTimeToUi(travel.time);
	if (scaledTime <= 0) return travel.destination;
	const percent = Math.min(Math.max(elapsedMs / scaledTime, 0), 1);
	return new XY(
		effect.xy.x + (travel.destination.x - effect.xy.x) * percent,
		effect.xy.y + (travel.destination.y - effect.xy.y) * percent,
	);
}

/**
 * Finds the active (non-expired, current animation order) MovementEffect for
 * a specific entity in the given EffectManager. Returns null if none found.
 */
export function findEntityMovementEffect(
	entity: PhysicalThing,
	manager: EffectManager,
): MovementEffect | null {
	const currentOrder = manager.getCurrentAnimationOrder();

	for (const effect of manager.getAllEffects()) {
		if (!(effect instanceof MovementEffect)) continue;
		if (effect.getAnimationOrder() !== currentOrder) continue;
		if (manager.isEffectExpired(effect)) continue;
		if (effect.thing !== entity) continue;
		return effect;
	}

	return null;
}

/** Lowest-order pending MovementEffect for the entity (order > currentOrder), or null. */
export function findEntityPendingMovementEffect(
	entity: PhysicalThing,
	manager: EffectManager,
): MovementEffect | null {
	const currentOrder = manager.getCurrentAnimationOrder();
	if (currentOrder === null) return null;

	let best: MovementEffect | null = null;
	for (const effect of manager.getAllEffects()) {
		if (!(effect instanceof MovementEffect)) continue;
		if (effect.thing !== entity) continue;
		if (effect.getAnimationOrder() <= currentOrder) continue;
		if (best === null || effect.getAnimationOrder() < best.getAnimationOrder()) {
			best = effect;
		}
	}
	return best;
}

/** True if the entity has any MovementEffect this turn, at any animation order. */
export function entityHasMovementEffect(entity: PhysicalThing, manager: EffectManager | undefined): boolean {
	if (!manager) return false;
	for (const effect of manager.getAllEffects()) {
		if (effect instanceof MovementEffect && effect.thing === entity) return true;
	}
	return false;
}

/**
 * Single source of truth for an entity's interpolated visual position in
 * fractional board coordinates. For each manager in order: if started, use the
 * active current-order MovementEffect (interpolated), else lock at the lowest
 * pending-order effect's origin. Returns the first hit, or null if no manager
 * yields a position. Callers apply their own coordinate conversion / fallback.
 */
export function resolveEntityVisualCell(
	entity: PhysicalThing,
	managers: (EffectManager | undefined)[],
): { x: number; y: number } | null {
	for (const manager of managers) {
		if (!manager || !manager.hasStarted()) continue;

		const current = findEntityMovementEffect(entity, manager);
		if (current) {
			return interpolateMovementBoard(current, getEffectPercentDone(current, manager));
		}

		// Pending move — lock at origin until the move's order arrives.
		const pending = findEntityPendingMovementEffect(entity, manager);
		if (pending) {
			return { x: pending.origin.x, y: pending.origin.y };
		}
	}

	return null;
}
